<template>
  <div class="wrapper">
    <loader v-if="getIsProjectCreating" :message="message" />
    <PastDueNotification />
    <notification-list />
    <context-menu v-if="!getIsEmpty" />
    <asset-modal v-if="getAssetModal !== null" />
    <audio-asset-modal v-if="getAudioAssetModal !== null" />
    <audio-asset-ref-modal v-if="getAudioAssetRefModal !== null" />
    <transition name="fade">
      <delete-asset-references-modal
        v-if="getDeleteAssetReferencesModal !== null"
      />
    </transition>
    <transition name="fade">
      <prototype-audio-modal v-if="getPrototypeAudioModal !== null" />
    </transition>
    <transition name="fade">
      <error-modal v-if="getErrorModal" :status-code="getErrorModal" />
    </transition>
    <transition name="fade">
      <board-properties-modal v-if="getBoardPropertiesModal !== null" />
    </transition>
    <transition name="fade">
      <component-properties-modal v-if="getComponentPropertiesModal !== null" />
    </transition>
    <transition name="fade">
      <export-modal v-if="getExportModal === true" />
    </transition>
    <transition name="fade">
      <global-vars-modal v-if="getGlobalVarsModal" />
    </transition>
    <UpgradeModal v-model:open="upgradeModal.open">
      <template #upgradeMessage>
        <Alert
          :title="upgradeModal.title"
          :subtitle="upgradeModal.subtitle"
          class="mb-6"
        />
      </template>
    </UpgradeModal>
    <transition name="fade">
      <confirmation-modal
        v-if="getConfirmationModal"
        :content="getConfirmationModal.content"
        :confirm-button="getConfirmationModal.confirmButton"
        :cancel-button="getConfirmationModal.cancelButton"
        :action="getConfirmationModal.action"
        :params="getConfirmationModal.params"
      />
    </transition>
    <router-view />
    <Toaster rich-colors />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { Toaster } from 'vue-sonner';
import { UpgradeModal } from '@components/common/UpgradeModal';
import emitter from 'tiny-emitter/instance';
import { EVENT } from '@/constants.js';
import { Alert } from '@ui/components/alert';
import PastDueNotification from './PastDueNotification.vue';
import Loader from './Loader.vue';
import GlobalVarsModal from '../modals/GlobalVarsModal/GlobalVarsModal.vue';
import NotificationList from './NotificationList.vue';
import ContextMenu from '../context-menu/ContextMenu.vue';
import DeleteAssetReferencesModal from '../modals/DeleteAssetReferencesModal.vue';
import ConfirmationModal from '../modals/ConfirmationModal.vue';
import AssetModal from '../modals/AssetModal.vue';
import BoardPropertiesModal from '../modals/BoardPropertiesModal/BoardPropertiesModal.vue';
import ErrorModal from '../modals/ErrorModal.vue';
import ExportModal from '../modals/ExportModal/ExportModal.vue';
import ComponentPropertiesModalVue from '../modals/ComponentPropertiesModal/ComponentPropertiesModal.vue';
import AudioAssetModal from '../modals/AudioAssetModal.vue';
import AudioAssetRefModal from '../modals/AudioAssetRefModal.vue';
import PrototypeAudioModal from '../modals/PrototypeAudioModal.vue';

export default {
  components: {
    loader: Loader,
    'notification-list': NotificationList,
    'context-menu': ContextMenu,
    'global-vars-modal': GlobalVarsModal,
    'board-properties-modal': BoardPropertiesModal,
    'component-properties-modal': ComponentPropertiesModalVue,
    'export-modal': ExportModal,
    'error-modal': ErrorModal,
    'asset-modal': AssetModal,
    'audio-asset-modal': AudioAssetModal,
    'audio-asset-ref-modal': AudioAssetRefModal,
    'delete-asset-references-modal': DeleteAssetReferencesModal,
    'confirmation-modal': ConfirmationModal,
    'prototype-audio-modal': PrototypeAudioModal,
    Toaster,
    UpgradeModal,
    Alert,
    PastDueNotification,
  },

  data() {
    return {
      upgradeModal: {
        open: false,
        title: '',
        subtitle: '',
      },
    };
  },

  computed: {
    ...mapGetters('application/modals', [
      'getBoardPropertiesModal',
      'getComponentPropertiesModal',
      'getAssetModal',
      'getAudioAssetModal',
      'getAudioAssetRefModal',
      'getPrototypeAudioModal',
      'getGlobalVarsModal',
      'getDeleteAssetReferencesModal',
      'getErrorModal',
      'getExportModal',
      'getConfirmationModal',
    ]),

    ...mapGetters('project', ['getHash']),
    ...mapGetters('user/account', ['getEmail', 'getId']),
    ...mapGetters('application/appStates', [
      'getIsProjectCreating',
      'getTemplateCreating',
    ]),
    ...mapGetters('application/contextMenu', ['getIsEmpty']),

    message() {
      if (this.getTemplateCreating === 'empty') {
        return 'Please wait while we are creating your project...';
      }
      if (this.getTemplateCreating === 'sample') {
        return 'Please wait a few seconds while we are setting up an example project...';
      }
      return 'Just wait a bit';
    },
  },

  created() {
    const { hash } = this.$route.params;
    document.execCommand('defaultParagraphSeparator', false, 'p'); // Setup unified paragraph elements for all contenteditables across all browsers
    this.startUserUpdateChannel({ id: this.getId });
  },

  beforeUnmount() {
    this.leaveUserUpdateChannel({ id: this.getId });
    emitter.off(EVENT.LIMIT_MODAL, this.onLimitModal);
  },

  mounted() {
    emitter.on(EVENT.LIMIT_MODAL, this.onLimitModal);
  },

  methods: {
    ...mapActions('user', ['setAccount']),
    ...mapActions('application/channels', [
      'startUserUpdateChannel',
      'leaveUserUpdateChannel',
    ]),

    onLimitModal({ open = true, title = '', subtitle = '' }) {
      this.upgradeModal.open = open;
      this.upgradeModal.title = title;
      this.upgradeModal.subtitle = subtitle;
    },
  },
};
</script>

<style scoped lang="scss">
.wrapper {
  // position: absolute;
  // top: 0;
  // right: 0;
  // bottom: 0;
  // left: 0;
  display: flex;
  flex-flow: column;
  height: 100%;
}
</style>

<style lang="scss">
//TRANSITIONS
.overlay.fade-enter-active,
.overlay.fade-leave-active {
  transition: opacity 0.2s ease;

  .modal {
    transition: transform 0.2s;
    transform-origin: center 100px;
    transform: scale(1);
  }
}

.overlay.fade-enter-from,
.overlay.fade-leave-to {
  opacity: 0;

  .modal {
    transform-origin: center 100px;
    transition: transform 0.2s;
    transform: scale(0.75);
  }
}
</style>
